import * as yup from 'yup';
import { StaticDataModel } from '../../../Component/Model/Dto/StaticData';
import { addMonths, isAfter } from 'date-fns';

const scoreLengthMessage = "cannot be be more than 16 digits long and is limited to 2 decimal points";

const validateScore = (value?: number | null) => {
  if (!value) return true; 
  const stringValue = value.toString(); 
  return stringValue.length <= 16 && /^\d{1,16}(\.\d{0,2})?$/.test(stringValue);
}

export const ScorecardTypeSelectionSchema = yup.object().shape({
    scorecardType: yup.object().shape({
      Id: yup.string(),
      Name: yup.string(),
    })
    .nullable().required('A scorecard type is required'),
    
    customType: yup.string().when("scorecardType", {
      is: (scorecardType: StaticDataModel) => (scorecardType !== null && scorecardType.Id === "0"),
      then: yup.string().required("Please enter a scorecard type name").nullable()
    })
  });

export const CertificateRegistryModalSchema = yup.object().shape({
  companyName: yup
  .string()
  .nullable()
  .required('Company Name is required')
  .max(255, 'Company Name must be less than 255 characters'),

  registrationNumber: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(100, 'Registration Number must be less than 100 characters'),

  vatNumber: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(10, 'VAT Number must be less than 10 characters')
  .matches(/^\d+$/, 'VAT Number should have digits only'),

  bbbeeLevelAchieved: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .required('B-BBEE Level Achieved is required')
  .max(150, 'B-BBEE Level Achieved must be less than 150 characters'),
 
  natureOfBusiness: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'Nature Of Business must be less than 150 characters'),
  
  measurementPeriodStartDate: yup
  .date()
  .nullable()
  .typeError('Measurement Period Start Date is required')
  .required('Measurement Period Start Date is required'),

  measurementPeriodEndDate: yup
  .date()
  .nullable()
  .typeError('Measurement Period End Date is required')
  .required('Measurement Period End Date is required')
  .test(
    'is-valid-measurement-period',
    'Measurement Period cannot be more than 12 months',
    function (endDate, { parent }) {
      const startDate = parent.measurementPeriodStartDate;

      if (startDate && endDate) {
        const maxEndDate = addMonths(startDate, 12);

        return isAfter(endDate, startDate) && isAfter(maxEndDate, endDate);
      }

      return true;
    }
  ),

  certificateNumber: yup
  .string()
  .nullable()
  .required('Certificate Number is required')
  .max(150, 'Certificate Number must be less than 150 characters'),

  versionNumber: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'Version Number must be less than 150 characters'),

  issueDate: yup
  .date()
  .nullable()
  .typeError('Issue Date is required')
  .required('Issue Date is required'),

  expiryDate: yup
  .date()
  .nullable()
  .typeError('Expiry Date is required')
  .required('Expiry Date is required'),

  totalPoints: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Total Points Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Total Points Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Total Points Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Total Points Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  bbbeeRecognitionPercentage: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'B-BBEE Recognition Percentage must be less than 150 characters'),

  sectorCodes: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'Sector Codes must be less than 150 characters'),

  subSectorCodes: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'Subsector Codes must be less than 150 characters'),
  
  procurementRecognitionLevel: yup
  .string()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .max(150, 'Procurement Recognition Level must be less than 150 characters'),

  equityOwnershipAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Ownership Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Ownership Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Ownership Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Ownership Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),
  
  managementControlAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Management Control Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Management Control Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Management Control Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Management Control Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  skillsDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Skills Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Skills Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Skills Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Skills Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  enterpriseDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Enterprise Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Enterprise Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Enterprise Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Enterprise Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  socioEconomicDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Socio-Economic Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Socio-Economic Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Socio-Economic Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Socio-Economic Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  accessToFinancialServicesAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Access to Financial Services Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Access to Financial Services Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Access to Financial Services Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Access to Financial Services Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  empowermentFinancingAndEnterpriseAndSupplierDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Empowerment Financing & Enterprise & Supplier Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Empowerment Financing & Enterprise & SupplierDevelopment Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Empowerment Financing & Enterprise & SupplierDevelopment Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Empowerment Financing & Enterprise & Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  preferentialProcurementAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Preferential Procurement Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Preferential Procurement Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Preferential Procurement Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Preferential Procurement Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  supplierDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Supplier Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Supplier Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  preferentialProcurementAndSupplierDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Preferential Procurement & Supplier Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Preferential Procurement & Supplier Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Preferential Procurement & Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Preferential Procurement & Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  enterpriseAndSupplierDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Enterprise & Supplier Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Enterprise & Supplier Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Enterprise & Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Enterprise & Supplier Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  economicDevelopmentAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Economic Development Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Economic Development Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Economic Development Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Economic Development Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  employmentEquityAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Employment Equity Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Employment Equity Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Employment Equity Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Employment Equity Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  rsmAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'RSM Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'RSM Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `RSM Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `RSM Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  empowermentFinancingAndESDAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Empowerment Financing & ESD Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Empowerment Financing & ESD Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Empowerment Financing & ESD Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Empowerment Financing & ESD Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  localisationAchievedScore: yup
  .mixed()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .test('is-numeric', 'Localisation Achieved Score must be a numeric value', value => {
    return value == null || !isNaN(value);
  })
  .test('is-valid', 'Localisation Achieved Score cannot be less than 0', value => {
    return value == null || value >= 0;
  })
  .test('is-valid', `Localisation Achieved Score ${scoreLengthMessage}`, value => {
    return value == null || value <= Number.MAX_SAFE_INTEGER;
  })
  .test('is-decimal', `Localisation Achieved Score ${scoreLengthMessage}`, value => {
    return validateScore(value);
  }),

  percentBlackOwnershipVotingRights: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Ownership Voting Rights must be a numeric value')
  .min(0, '% Black Ownership Voting Rights cannot be less than 0%')
  .max(100, '% Black Ownership Voting Rights cannot be more than 100%'),

  percentBlackOwnershipEconomicInterest: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Ownership Economic Interest must be a numeric value')
  .min(0, '% Black Ownership Economic Interest cannot be less than 0%')
  .max(100, '% Black Ownership Economic Interest cannot be more than 100%'),

  percentBlackWomenOwnershipVotingRights: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Women Ownership Voting Rights must be a numeric value')
  .min(0, '% Black Women Ownership Voting Rights cannot be less than 0%')
  .max(100, '% Black Women Ownership Voting Rights cannot be more than 100%'),

  percentBlackWomenOwnershipEconomicInterest: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Women Ownership Economic Interest must be a numeric value')
  .min(0, '% Black Women Ownership Economic Interest cannot be less than 0%')
  .max(100, '% Black Women Ownership Economic Interest cannot be more than 100%'),

  blackOwnershipTotal: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('Black Ownership Total must be a numeric value')
  .min(0, 'Black Ownership Total cannot be less than 0%')
  .max(100, '% Black Ownership Total cannot be more than 100%'),

  percentBlackDesignatedGroups: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Designated Groups must be a numeric value')
  .min(0, '% Black Designated Groups cannot be less than 0%')
  .max(100, '% Black Designated Groups cannot be more than 100%'),

  blackYouthShareholdingPercentage: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Youth must be a numeric value')
  .min(0, '% Black Youth cannot be less than 0%')
  .max(100, '% Black Youth cannot be more than 100%'),

  blackDisabledShareholdingPercentage: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Disabled must be a numeric value')
  .min(0, '% Black Disabled cannot be less than 0%')
  .max(100, '% Black Disabled cannot be more than 100%'),

  blackMilitaryVeteransShareholdingPercentage: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Military Veterans must be a numeric value')
  .min(0, '% Black Military Veterans cannot be less than 0%')
  .max(100, '% Black Military Veterans cannot be more than 100%'),

  blackNewEntrants: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black New Entrants must be a numeric value')
  .min(0, '% Black New Entrants cannot be less than 0%')
  .max(100, '% Black New Entrants cannot be more than 100%'),

  blackPeopleLivingInRuralAreasShareholdingPercentage: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black People Living in Rural Areas must be a numeric value')
  .min(0, '% Black People Living in Rural Areas cannot be less than 0%')
  .max(100, '% Black People Living in Rural Areas cannot be more than 100%'),

  blackUnemployedShareholdingPercentage: yup
  .number()
  .nullable()
  .transform((curr, orig) => (orig === '' ? null : curr))
  .typeError('% Black Unemployed must be a numeric value')
  .min(0, '% Black Unemployed cannot be less than 0%')
  .max(100, '% Black Unemployed cannot be more than 100%')
});